import { useState, type ChangeEvent, type FormEvent } from 'react'
import { ExternalLink, Save } from 'lucide-react'
import { updateSystemSettings, type SystemSettings } from '../api/settings'
import { Button } from './ui/Button'

const MAX_LOGO_BYTES = 256 * 1024
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/gif']
const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border border-border-primary bg-surface-secondary text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

function validLogo(value: string): boolean {
  if (value === '') return true
  if (/^\/(?!\/)[^\\\s]*$/.test(value) && Array.from(value).every(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127)) return true
  const encoded = /^data:image\/(?:png|jpeg|gif);base64,([A-Za-z0-9+/]+={0,2})$/.exec(value)?.[1]
  if (!encoded || encoded.length % 4 !== 0 || encoded.length > Math.ceil(MAX_LOGO_BYTES / 3) * 4) return false
  try {
    return atob(encoded).length <= MAX_LOGO_BYTES
  } catch {
    return false
  }
}

export function StatusPageSettings({ settings }: { settings: SystemSettings }) {
  const [title, setTitle] = useState(settings.status_page_title)
  const [description, setDescription] = useState(settings.status_page_description)
  const [logo, setLogo] = useState(settings.status_page_logo_url)
  const [accent, setAccent] = useState(settings.status_page_accent_color)
  const [saving, setSaving] = useState(false)
  const [readingLogo, setReadingLogo] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSaved(false)
    setError('')
    if (accent.trim() && !/^#[0-9a-fA-F]{6}$/.test(accent.trim())) {
      setError('Accent colour must be a six-digit hex colour, such as #2563eb.')
      return
    }
    if (!validLogo(logo.trim())) {
      setError('Use a same-origin image path or a PNG, JPEG or GIF upload up to 256 KiB.')
      return
    }
    setSaving(true)
    try {
      await updateSystemSettings({
        status_page_title: title.trim(),
        status_page_description: description.trim(),
        status_page_logo_url: logo.trim(),
        status_page_accent_color: accent.trim(),
      })
      setSaved(true)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Failed to save status page branding')
    } finally {
      setSaving(false)
    }
  }

  function uploadLogo(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setError('')
    setSaved(false)
    if (!LOGO_TYPES.includes(file.type) || file.size > MAX_LOGO_BYTES || file.size === 0) {
      setError('Choose a PNG, JPEG or GIF image up to 256 KiB.')
      return
    }
    setReadingLogo(true)
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string' && validLogo(reader.result)) {
        setLogo(reader.result)
      } else {
        setError('Unable to read this logo. Choose a PNG, JPEG or GIF image.')
      }
      setReadingLogo(false)
    }
    reader.onerror = () => {
      setError('Unable to read this logo. Try choosing the file again.')
      setReadingLogo(false)
    }
    reader.readAsDataURL(file)
  }

  return (
    <section className="bg-surface-primary border border-border-primary rounded-xl p-6 space-y-5" aria-labelledby="status-page-settings-title">
      <div>
        <h2 id="status-page-settings-title" className="text-base font-semibold text-text-primary">Status Page</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          A public, read-only page showing incidents you explicitly publish. No login required.
        </p>
      </div>
      <a href="/status" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-brand-primary hover:underline">
        <ExternalLink className="w-4 h-4" />
        {window.location.origin}/status
      </a>
      <form onSubmit={save} className="space-y-4">
        <fieldset disabled={saving || readingLogo} className="space-y-4">
          <div>
            <label htmlFor="status-page-title" className="block text-sm font-medium text-text-primary mb-1">Page title</label>
            <input id="status-page-title" value={title} onChange={(event) => { setTitle(event.target.value); setSaved(false) }} maxLength={120} placeholder={`${settings.instance_name || 'Regen'} Status`} className={INPUT_CLASS} />
          </div>
          <div>
            <label htmlFor="status-page-description" className="block text-sm font-medium text-text-primary mb-1">Description</label>
            <textarea id="status-page-description" value={description} onChange={(event) => { setDescription(event.target.value); setSaved(false) }} maxLength={500} rows={3} placeholder="Real-time incident status" className={INPUT_CLASS} />
          </div>
          <div>
            <label htmlFor="status-page-logo" className="block text-sm font-medium text-text-primary mb-1">Logo image path</label>
            <input id="status-page-logo" value={logo.startsWith('data:') ? '' : logo} onChange={(event) => { setLogo(event.target.value); setSaved(false) }} placeholder="/images/status-logo.png" aria-describedby="status-page-logo-help" className={INPUT_CLASS} />
            <p id="status-page-logo-help" className="mt-1 text-xs text-text-tertiary">Use a same-origin path beginning with /, or upload a PNG, JPEG or GIF up to 256 KiB.</p>
            <label htmlFor="status-page-logo-upload" className="block text-sm text-text-secondary mt-3 mb-1">Upload logo</label>
            <input id="status-page-logo-upload" type="file" accept="image/png,image/jpeg,image/gif" onChange={uploadLogo} className="block max-w-full text-sm text-text-secondary" />
            {logo && validLogo(logo) && (
              <div className="flex items-center gap-3 mt-3">
                <img src={logo} alt="Status page logo preview" className="max-h-16 max-w-40 object-contain" referrerPolicy="no-referrer" />
                <Button type="button" variant="ghost" size="sm" onClick={() => { setLogo(''); setSaved(false) }}>Remove logo</Button>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="status-page-accent" className="block text-sm font-medium text-text-primary mb-1">Accent colour</label>
            <input id="status-page-accent" value={accent} onChange={(event) => { setAccent(event.target.value); setSaved(false) }} placeholder="#2563eb" maxLength={7} aria-describedby="status-page-defaults" className={INPUT_CLASS} />
          </div>
          <p id="status-page-defaults" className="text-xs text-text-tertiary">Leave title, description or accent colour blank to use the defaults.</p>
        </fieldset>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={readingLogo} loading={saving} size="sm"><Save className="w-4 h-4" />Save status page</Button>
          {readingLogo && <span role="status" className="text-sm text-text-secondary">Reading logo…</span>}
          {saved && <span role="status" className="text-sm text-green-600">Saved</span>}
        </div>
      </form>
    </section>
  )
}
